/* eslint-disable no-invalid-this */
import { Data, Effect, Equivalence } from 'effect';
import { type Dayjs } from 'dayjs';

import { OnlineCartDto } from './online-cart.js';
import { VoidResult } from '../../../utility/result.js';
import { OperationResponse } from '../../core/operation-response.js';
import { EventProfile } from '../event-profile.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { Voucher } from '../discounts/voucher.js';
import { BusinessPromoCode } from '../discounts/business-promo-code.js';
import { EventPromoCode } from '../discounts/event-promo-code.js';
import { OpenTicketPass } from '../discounts/open-ticket-pass.js';
import { TicketGiftCard } from '../discounts/ticket-gift-card.js';
import { TrackingPromoCode } from '../discounts/tracking-promo-code.js';
import { DonationCartItemDto } from './donation-cart-item.js';
import { ProductCartItemDto } from './product-cart-item.js';
import { ProductListComponent } from '../../../components/events/product-list-component.js';
import { Product } from '../product.js';
import { TicketCartItemDto } from './ticket-cart-item.js';
import { TicketCartItemWithQuantityDto } from './ticket-cart-item-with-quantity.js';
import { TicketTemplate } from '../ticket-template/ticket-template.js';
import { TicketSpecialOffer } from '../ticket-template/ticket-special-offer.js';
import { TicketTemplateStatus } from '../ticket-template/ticket-template-status.js';
import { TicketSupplementTemplate } from '../ticket-supplement-template.js';
import { CascadingTicketAvailabilityModifier } from '../ticket-availability-modifier.js';
import type { ITicketPool } from '../ticketing-state/ticket-pool.js';
import { EventTicketCapacityGroups } from '../event-ticket-capacity-groups.js';
import { SeatingLayout } from '../seating/seating-layout.js';
import { DateSpan } from '../date-span.js';
import { isSameData, isSameList } from '../../dto/data-model-utils.js';
import { TicketCartItemWithSeatingDto } from './ticket-cart-item-with-seating.js';
import { SeatingSettingsComponent } from '../../../components/events/seating-settings-component.js';
import { OrderCreationStrategy } from './order-creation-strategy.js';
import { assertUnreachable } from '../../../utility/general-utility.js';
import { CartItemDto } from './cart-item.js';
import { Component } from '../../../components/core/component.js';
import { ComponentList } from '../../../components/core/component-list.js';
import { FormInputField } from '../../../contracts/form-input-field.js';
import { FormSubmissionAnswer } from '../../../contracts/form-submission-answer.js';
import { TicketTemplateComponent } from '../../../components/events/ticket-template-component.js';
import { TicketCartLimitComponent } from '../../../components/ticket-template/ticket-cart-limit-component.js';
import { OpenTicketPassComponent } from '../../../components/voucher/open-ticket-pass-component.js';
import { TicketGiftCardComponent } from '../../../components/voucher/ticket-gift-card-component.js';
import { FormQuestionsComponent } from '../../../components/point-of-interest/form-questions-component.js';
import { BooleanFormInputField } from '../form-input-field/boolean-form-input-field.js';
import { DateTimeFormInputField } from '../form-input-field/date-time-form-input-field.js';
import { OptionSelectFormInputField } from '../form-input-field/option-select-form-input-field.js';
import { TextFormInputField } from '../form-input-field/text-form-input-field.js';
import { BooleanFormSubmissionAnswer } from '../form-submission/boolean-form-submission-answer.js';
import { DateTimeFormSubmissionAnswer } from '../form-submission/date-time-form-submission-answer.js';
import { OptionSelectFormSubmissionAnswer } from '../form-submission/option-select-form-submission-answer.js';
import { TextFormSubmissionAnswer } from '../form-submission/text-form-submission-answer.js';
import { TimeZone } from '../../../time/timezone.js';
import { CustomerAccount } from '../customer-account.js';
import { Optional } from '../../core/optional.js';
import { type FormSubmission } from '../form-submission/form-submission.js';
import { TicketAsPassEventBookingProtocol } from '../event-booking-protocol/ticket-as-pass-event-booking-protocol.js';
import { OpenAccessPassComponent } from '../../../components/voucher/open-access-pass-component.js';
import { OrderSideEffectType } from '../orders/order-side-effect-type.js';
import { CreateMultiOpenAccessPassSideEffect } from '../orders/create-multi-open-access-pass-side-effect.js';
import { DiscountCodeBase } from '../../../contracts/discount-code.js';
import { OpenAccessPassDetails } from '../open-access-pass/open-access-pass-details.js';
import { FlexiblePassProtocol } from '../open-access-pass/flexible-pass-protocol.js';
import { OpenAccessPass, OpenAccessPassStatus } from '../open-access-pass/open-access-pass.js';
import { GiftCardDetails } from '../gift-card/gift-card-details.js';
import { GiftCardStatus } from '../gift-card/gift-card.js';

type FetchEventArgs = {
  readonly businessRef: BusinessModuleRef,
  readonly eventId: string,
};

type FetchDiscountCodeArgs = {
  readonly businessRef: BusinessModuleRef,
  readonly code: string,
};

type FetchSeatingLayoutArgs = FetchEventArgs & {
  readonly seatingLayoutId: string,
};

type FormSubmissionAnswerTypeMismatch = {
  readonly expectedType: string,
  readonly actualType: string,
};

type FormSubmissionAnswerTypeMatcher = {
  readonly matchesQuestion: (question: FormInputField) => boolean,
  readonly matchesAnswer: (answer: FormSubmissionAnswer) => boolean,
  readonly expectedType: string,
};

type CartFormQuestionReferences = {
  readonly eventIds: readonly string[],
  readonly ticketIds: ReadonlySet<string>,
};

type FormQuestionCatalog = {
  readonly eventQuestionsById: ReadonlyMap<string, FormInputField>,
  readonly requiredEventQuestions: readonly FormInputField[],
  readonly ticketQuestionsByTicketId: ReadonlyMap<string, ReadonlyMap<string, FormInputField>>,
  readonly requiredTicketQuestionsByTicketId: ReadonlyMap<string, readonly FormInputField[]>,
};

type SubmittedFormQuestionIds = {
  readonly eventQuestionIds: ReadonlySet<string>,
  readonly ticketQuestionIdsByTicketId: ReadonlyMap<string, ReadonlySet<string>>,
};

type FormSubmissionAnswerValidationArgs = {
  readonly submissionIndex: number,
  readonly answers: readonly FormSubmissionAnswer[],
  readonly questionsById: ReadonlyMap<string, FormInputField> | undefined,
  readonly createQuestionMissingError: (questionId: string) => OnlineCartFormQuestionDoesNotExist,
  readonly createTypeMismatchError: (
    questionId: string,
    typeMismatch: FormSubmissionAnswerTypeMismatch,
  ) => OnlineCartHasMismatchedSubmissionAnswerType,
};

const formSubmissionAnswerTypeMatchers: readonly FormSubmissionAnswerTypeMatcher[] = [
  {
    matchesQuestion: (question) => question instanceof BooleanFormInputField,
    matchesAnswer: (answer) => answer instanceof BooleanFormSubmissionAnswer,
    expectedType: BooleanFormInputField.TYPE_ID,
  },
  {
    matchesQuestion: (question) => question instanceof DateTimeFormInputField,
    matchesAnswer: (answer) => answer instanceof DateTimeFormSubmissionAnswer,
    expectedType: DateTimeFormInputField.TYPE_ID,
  },
  {
    matchesQuestion: (question) => question instanceof OptionSelectFormInputField,
    matchesAnswer: (answer) => answer instanceof OptionSelectFormSubmissionAnswer,
    expectedType: OptionSelectFormInputField.TYPE_ID,
  },
  {
    matchesQuestion: (question) => question instanceof TextFormInputField,
    matchesAnswer: (answer) => answer instanceof TextFormSubmissionAnswer,
    expectedType: TextFormInputField.TYPE_ID,
  },
];

export abstract class OnlineCartValidationError {
  abstract get message(): string;
}

export class OnlineCartNoItemsError extends Data.TaggedError('OnlineCartNoItemsError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart must have items';
  }
}

export class OnlineCartEventDoesNotExistError extends Data.TaggedError('OnlineCartEventDoesNotExistError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart has an invalid eventId';
  }
}

export class OnlineCartDiscountCodeDoesNotExistError extends Data.TaggedError('OnlineCartDiscountCodeDoesNotExistError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart has an invalid discount code';
  }
}

export class OnlineCartDiscountCodeDoesNotMatchError extends Data.TaggedError('OnlineCartDiscountCodeDoesNotMatchError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart has a discount code that does not match the one in the database';
  }
}

export class OnlineCartUnsupportedDiscountCodeError extends Data.TaggedError('OnlineCartUnsupportedDiscountCodeError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart has an unsupported discount code';
  }
}

export class OnlineCartFlexiblePassInvalidProtocolError extends Data.TaggedError('OnlineCartFlexiblePassInvalidProtocolError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart has a flexible pass with an invalid protocol';
  }
}

export class OnlineCartFlexiblePassNotLiveError extends Data.TaggedError('OnlineCartFlexiblePassNotLiveError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart has a flexible pass that is not live';
  }
}

export class OnlineCartFlexiblePassNotYetValidError extends Data.TaggedError('OnlineCartFlexiblePassNotYetValidError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart has a flexible pass that is not yet valid';
  }
}

export class OnlineCartFlexiblePassExpiredError extends Data.TaggedError('OnlineCartFlexiblePassExpiredError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart has an expired flexible pass';
  }
}

export class OnlineCartFlexiblePassExhaustedError extends Data.TaggedError('OnlineCartFlexiblePassExhaustedError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart has an exhausted flexible pass';
  }
}

export class OnlineCartFlexiblePassIneligibleEventError extends Data.TaggedError('OnlineCartFlexiblePassIneligibleEventError') implements OnlineCartValidationError {
  constructor(public readonly eventId: string) {
    super();
  }

  public override get message(): string {
    return `Cart has a flexible pass that is not valid for event ${this.eventId}`;
  }
}

export class OnlineCartHasMoreThanOneDonationItemError extends Data.TaggedError('OnlineCartHasMoreThanOneDonationItemError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart has more than one donation item';
  }
}

export class OnlineCartHasInvalidDonationItemError extends Data.TaggedError('OnlineCartHasInvalidDonationItemError') implements OnlineCartValidationError {
  constructor(private readonly itemIndex: number, private readonly reason: string ) {
    super();
  }

  public override get message(): string {
    return `Cart has an invalid donation item at index ${this.itemIndex}: ${this.reason}`;
  }
}

export class OnlineCartProductItemEventDoesNotExistError extends Data.TaggedError('OnlineCartProductItemEventDoesNotExistError') implements OnlineCartValidationError {
  constructor(private readonly itemIndex: number) {
    super();
  }

  public override get message(): string {
    return `Cart has a product item at index ${this.itemIndex} with an eventId that does not exist`;
  }
}

export class OnlineCartInvalidProductItemError extends Data.TaggedError('OnlineCartInvalidProductItemError') implements OnlineCartValidationError {
  constructor(private readonly itemIndex: number, private readonly reason: string) {
    super();
  }

  public override get message(): string {
    return `Cart has an invalid product item at index ${this.itemIndex}: ${this.reason}`;
  }
}

export class OnlineCartInvalidTicketItemWithQuantityError extends Data.TaggedError('OnlineCartInvalidTicketItemWithQuantityError') implements OnlineCartValidationError {
  constructor(private readonly itemIndex: number, private readonly reason: string) {
    super();
  }

  public override get message(): string {
    return `Cart has an invalid ticket item with quantity at index ${this.itemIndex}: ${this.reason}`;
  }
}

export class OnlineCartTicketTemplateDoesNotExistError extends Data.TaggedError('OnlineCartTicketTemplateDoesNotExistError') implements OnlineCartValidationError {
  constructor(
    private readonly itemIndex: number,
    private readonly ticketTemplateId: string,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart has a ticket template item at index ${this.itemIndex} with ticketTemplateId ${this.ticketTemplateId} that does not exist`;
  }
}

export class OnlineCartTicketAsPassProtocolMissingPassTemplateIdError extends Data.TaggedError('OnlineCartTicketAsPassProtocolMissingPassTemplateIdError') implements OnlineCartValidationError {
  constructor(public readonly eventId: string) {
    super();
  }

  public override get message(): string {
    return `Cart references TicketAsPass event ${this.eventId} without passTemplateIds`;
  }
}

export class OnlineCartTicketAsPassCartTemplateMissingOpenAccessPassComponentError extends Data.TaggedError('OnlineCartTicketAsPassCartTemplateMissingOpenAccessPassComponentError') implements OnlineCartValidationError {
  constructor(
    public readonly eventId: string,
    public readonly itemIndex: number,
    public readonly ticketTemplateId: string,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart ticket template ${this.ticketTemplateId} at index ${this.itemIndex} references TicketAsPass event ${this.eventId} but has no OpenAccessPassComponent`;
  }
}

export class OnlineCartTicketAsPassPublishedTemplateMissingOpenAccessPassComponentError extends Data.TaggedError('OnlineCartTicketAsPassPublishedTemplateMissingOpenAccessPassComponentError') implements OnlineCartValidationError {
  constructor(
    public readonly eventId: string,
    public readonly itemIndex: number,
    public readonly ticketTemplateId: string,
  ) {
    super();
  }

  public override get message(): string {
    return `Published ticket template ${this.ticketTemplateId} referenced by cart item at index ${this.itemIndex} for TicketAsPass event ${this.eventId} has no OpenAccessPassComponent`;
  }
}

export type OnlineCartTicketAsPassTemplateSource = 'cart' | 'publishedEvent';

export class OnlineCartTicketAsPassTemplateIdMismatchError extends Data.TaggedError('OnlineCartTicketAsPassTemplateIdMismatchError') implements OnlineCartValidationError {
  constructor(
    public readonly source: OnlineCartTicketAsPassTemplateSource,
    public readonly eventId: string,
    public readonly itemIndex: number,
    public readonly ticketTemplateId: string,
    public readonly expectedPassTemplateIds: string[],
    public readonly actualPassTemplateIds: string[],
  ) {
    super();
  }

  public override get message(): string {
    return `${this.source === 'cart' ? 'Cart' : 'Published'} ticket template ${this.ticketTemplateId} referenced by cart item at index ${this.itemIndex} for TicketAsPass event ${this.eventId} has passTemplateId ${this.actualPassTemplateIds.join(',')}, expected [${this.expectedPassTemplateIds.join(',')}]`;
  }
}

export class OnlineCartTicketTemplateDoesNotMatchError extends Data.TaggedError('OnlineCartTicketTemplateDoesNotMatchError') implements OnlineCartValidationError {
  constructor(
    private readonly itemIndex: number,
    private readonly ticketTemplateId: string,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart has a ticket template item at index ${this.itemIndex} with ticketTemplateId ${this.ticketTemplateId} that does not match the one in the database`;
  }
}

export class OnlineCartTicketTemplateNotLiveError extends Data.TaggedError('OnlineCartTicketTemplateNotLiveError') implements OnlineCartValidationError {
  constructor(
    private readonly itemIndex: number,
    private readonly ticketTemplateId: string,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart has a ticket template item at index ${this.itemIndex} with ticketTemplateId ${this.ticketTemplateId} that is not live`;
  }
}

export class OnlineCartTicketTemplateNotPurchasableError extends Data.TaggedError('OnlineCartTicketTemplateNotPurchasableError') implements OnlineCartValidationError {
  constructor(
    private readonly itemIndex: number,
    private readonly ticketTemplateId: string,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart has a ticket template item at index ${this.itemIndex} with ticketTemplateId ${this.ticketTemplateId} that is not purchasable`;
  }
}

export class OnlineCartTicketTemplateNotInSellingDateRangeError extends Data.TaggedError('OnlineCartTicketTemplateNotInSellingDateRangeError') implements OnlineCartValidationError {
  constructor(
    private readonly itemIndex: number,
    private readonly ticketTemplateId: string,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart has a ticket template item at index ${this.itemIndex} with ticketTemplateId ${this.ticketTemplateId} that is not in selling date range`;
  }
}

export class OnlineCartTicketTemplateCartLimitExceededError extends Data.TaggedError('OnlineCartTicketTemplateCartLimitExceededError') implements OnlineCartValidationError {
  constructor(
    private readonly ticketTemplateId: string,
    private readonly cartLimit: number,
    private readonly ticketCount: number,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart has ticketTemplateId ${this.ticketTemplateId} with ticket count ${this.ticketCount} exceeding cart limit ${this.cartLimit}`;
  }
}

export class OnlineCartCoupleTicketQuantityIsOddError extends Data.TaggedError('OnlineCartCoupleTicketQuantityIsOddError') implements OnlineCartValidationError {
  constructor(
    private readonly ticketTemplateId: string,
    private readonly ticketCount: number,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart has couple ticketTemplateId ${this.ticketTemplateId} with odd ticket count ${this.ticketCount}`;
  }
}

export class OnlineCartTicketSupplementTemplateDoesNotExistError extends Data.TaggedError('OnlineCartTicketSupplementTemplateDoesNotExistError') implements OnlineCartValidationError {
  constructor(
    private readonly itemIndex: number,
    private readonly ticketSupplementTemplateId: string,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart has a ticket supplement template item at index ${this.itemIndex} with ticketSupplementTemplateId ${this.ticketSupplementTemplateId} that does not exist`;
  }
}

export class OnlineCartTicketSupplementTemplateDoesNotMatchError extends Data.TaggedError('OnlineCartTicketSupplementTemplateDoesNotMatchError') implements OnlineCartValidationError {
  constructor(
    private readonly itemIndex: number,
    private readonly ticketSupplementTemplateId: string,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart has a ticket supplement template item at index ${this.itemIndex} with ticketSupplementTemplateId ${this.ticketSupplementTemplateId} that does not match the one in the database`;
  }
}

export class OnlineCartSeatingLayoutDoesNotExistError extends Data.TaggedError('OnlineCartSeatingLayoutDoesNotExistError') implements OnlineCartValidationError {
  constructor(
    private readonly itemIndex: number,
    private readonly seatingLayoutId: string,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart has a seating layout item at index ${this.itemIndex} with seatingLayoutId ${this.seatingLayoutId} that does not exist`;
  }
}

export class OnlineCartSeatDoesNotExistError extends Data.TaggedError('OnlineCartSeatDoesNotExistError') implements OnlineCartValidationError {
  constructor(
    private readonly itemIndex: number,
    private readonly seatId: number,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart has a seat at index ${this.itemIndex} with seatId ${this.seatId} that does not exist in the seating layout`;
  }
}

export class OnlineCartItemsIncompatibleWithOrderCreationStrategyError extends Data.TaggedError('OnlineCartItemsIncompatibleWithOrderCreationStrategyError') implements OnlineCartValidationError {
  constructor(private readonly itemIndex: number, private readonly strategy: OrderCreationStrategy) {
    super();
  }

  public override get message(): string {
    return `Cart has an item at index ${this.itemIndex} that is incompatible with the order creation strategy ${this.strategy}.`;
  }
}

export class OnlineCartFormQuestionDoesNotExist extends Data.TaggedError('OnlineCartFormQuestionDoesNotExistOnEvent') implements OnlineCartValidationError {
  private constructor(
    private readonly submissionIndex: number,
    private readonly formQuestionId: string,
    private readonly ticketTemplateId: string | null,
  ) {
    super();
  }

  static eventLevel(submissionIndex: number, formQuestionId: string): OnlineCartFormQuestionDoesNotExist {
    return new OnlineCartFormQuestionDoesNotExist(submissionIndex, formQuestionId, null);
  }

  static ticketLevel(submissionIndex: number, formQuestionId: string, ticketTemplateId: string): OnlineCartFormQuestionDoesNotExist {
    return new OnlineCartFormQuestionDoesNotExist(submissionIndex, formQuestionId, ticketTemplateId);
  }

  public override get message(): string {
    if(this.ticketTemplateId == null) {
      return `Cart has an event level form submission at index ${this.submissionIndex} with formQuestionId ${this.formQuestionId} that does not exist on the event`;
    }
    else {
      return `Cart has a ticket template level form submission at index ${this.submissionIndex} with formQuestionId ${this.formQuestionId} that does not exist on the ticket template with ticketTemplateId ${this.ticketTemplateId}`;
    }
  }
}

export class OnlineCartDoesNotHaveSubmissionAnswerForRequiredQuestion extends Data.TaggedError('OnlineCartDoesNotHaveSubmissionAnswerForRequiredEventLevelQuestion') implements OnlineCartValidationError {
  private constructor(
    private readonly formQuestionId: string,
    private readonly ticketTemplateId: string | null,
  ) {
    super();
  }

  static eventLevel(formQuestionId: string): OnlineCartDoesNotHaveSubmissionAnswerForRequiredQuestion {
    return new OnlineCartDoesNotHaveSubmissionAnswerForRequiredQuestion(formQuestionId, null);
  }

  static ticketLevel(formQuestionId: string, ticketTemplateId: string): OnlineCartDoesNotHaveSubmissionAnswerForRequiredQuestion {
    return new OnlineCartDoesNotHaveSubmissionAnswerForRequiredQuestion(formQuestionId, ticketTemplateId);
  }

  public override get message(): string {
    if(this.ticketTemplateId == null) {
      return `Cart does not have a submission answer for the required event level form question with formQuestionId ${this.formQuestionId}`;
    }
    else {
      return `Cart does not have a submission answer for the required ticket template level form question with formQuestionId ${this.formQuestionId} on ticket template with ticketTemplateId ${this.ticketTemplateId}`;
    }
  }
}

export class OnlineCartHasMismatchedSubmissionAnswerType extends Data.TaggedError('OnlineCartHasMismatchedSubmissionAnswerTypeForEventLevelQuestion') implements OnlineCartValidationError {
  private constructor(
    private readonly submissionIndex: number,
    private readonly formQuestionId: string,
    private readonly expectedAnswerType: string,
    private readonly actualAnswerType: string,
    private readonly ticketTemplateId: string | null,
  ) {
    super();
  }

  static eventLevel(submissionIndex: number, formQuestionId: string, expectedAnswerType: string, actualAnswerType: string): OnlineCartHasMismatchedSubmissionAnswerType {
    return new OnlineCartHasMismatchedSubmissionAnswerType(submissionIndex, formQuestionId, expectedAnswerType, actualAnswerType, null);
  }

  static ticketLevel(submissionIndex: number, formQuestionId: string, expectedAnswerType: string, actualAnswerType: string, ticketTemplateId: string): OnlineCartHasMismatchedSubmissionAnswerType {
    return new OnlineCartHasMismatchedSubmissionAnswerType(submissionIndex, formQuestionId, expectedAnswerType, actualAnswerType, ticketTemplateId);
  }

  public override get message(): string {
    if(this.ticketTemplateId == null) {
      return `Cart has a mismatched submission answer type at index ${this.submissionIndex} for event level question with formQuestionId ${this.formQuestionId}. Expected: ${this.expectedAnswerType}, Actual: ${this.actualAnswerType}`;
    }
    else {
      return `Cart has a mismatched submission answer type at index ${this.submissionIndex} for ticket template level question with formQuestionId ${this.formQuestionId} on ticket template with ticketTemplateId ${this.ticketTemplateId}. Expected: ${this.expectedAnswerType}, Actual: ${this.actualAnswerType}`;
    }
  }
}

export class OnlineCartUnexpectedError extends Data.TaggedError('OnlineCartUnexpectedError') implements OnlineCartValidationError {
  constructor(private readonly error: Error) {
    super();
  }

  public override get message(): string {
    return `Cart validation failed with an unexpected error: ${JSON.stringify(this.error, null, 2)}`;
  }
}

export class OnlineCartCustomerAccountDoesNotExistError extends Data.TaggedError('OnlineCartCustomerAccountDoesNotExistError') implements OnlineCartValidationError {
  constructor(private readonly authenticatedUserId: string) {
    super();
  }

  public override get message(): string {
    return `Cart has an authenticatedUserId ${this.authenticatedUserId} that does not belong to a customer account`;
  }
}

export class OnlineCartCreatorDoesNotMatchAuthenticatedUserError extends Data.TaggedError('OnlineCartCreatorDoesNotMatchAuthenticatedUserError') implements OnlineCartValidationError {
  constructor(
    private readonly authenticatedUserId: string,
    private readonly creatorId: string | null,
  ) {
    super();
  }

  public override get message(): string {
    return `Cart creatorId ${this.creatorId} does not match authenticatedUserId ${this.authenticatedUserId}`;
  }
}

export class OnlineCartAuthenticatedOrderIsGuestOrderError extends Data.TaggedError('OnlineCartAuthenticatedOrderIsGuestOrderError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart must not be marked as a guest order when authenticatedUserId is provided';
  }
}

export class OnlineCartGuestOrderExpectedError extends Data.TaggedError('OnlineCartGuestOrderExpectedError') implements OnlineCartValidationError {
  public override get message(): string {
    return 'Cart must be a guest order when authenticatedUserId is not provided';
  }
}

export class OnlineCartEventIdUndecidableForProductItemError extends Data.TaggedError('OnlineCartEventIdUndecidableForProductItemError') implements OnlineCartValidationError {
  constructor(private readonly itemIndex: number) {
    super();
  }

  public override get message(): string {
    return `Cart has a product item at index ${this.itemIndex} with an eventId that is null and the cart's eventId is also null, making it impossible to determine the eventId for the product item`;
  }
}

export class OnlineCartInvalidCustomerProfileError extends Data.TaggedError('OnlineCartInvalidCustomerProfileError') implements OnlineCartValidationError {
  constructor(private readonly reason: string) {
    super();
  }

  public override get message(): string {
    return `Cart has an invalid customer profile: ${this.reason}`;
  }
}

export class OnlineCartValidator {
  private static readonly CASCADING_AVAILABILITY_REMAINING_THRESHOLD = 5;

  private constructor(
    private readonly now: Dayjs,
    private readonly authenticatedUserId: string | null,
    private readonly cart: Readonly<OnlineCartDto>,
    private readonly fetchEvent: (businessRef: BusinessModuleRef, eventId: string) => Effect.Effect<EventProfile, Error, never>,
    private readonly fetchPublishedEvent: (eventId: string) => Effect.Effect<EventProfile, Error, never>,
    private readonly fetchDiscountCodes: (businessRef: BusinessModuleRef, code: string) => Effect.Effect<DiscountCodeBase[], Error, never>,
    private readonly fetchTicketSupplementTemplates: (businessRef: BusinessModuleRef) => Effect.Effect<TicketSupplementTemplate[], Error, never>,
    private readonly fetchSeatingLayoutForEvent: (businessRef: BusinessModuleRef, eventId: string, seatingLayoutId: string) => Effect.Effect<SeatingLayout, Error, never>,
    private readonly fetchCustomerAccount: (accountId: string) => Effect.Effect<CustomerAccount, Error, never>,
    private readonly fetchTicketPoolIfAvailable: (businessRef: BusinessModuleRef, eventId: string) => Effect.Effect<Optional<ITicketPool>, Error, never>,
    private readonly fetchTicketCapacityGroups: (businessRef: BusinessModuleRef, eventId: string) => Effect.Effect<EventTicketCapacityGroups, Error, never>,
  ) {}

  static create(
      now: Dayjs,
      authenticatedUserId: string | null,
      cart: Readonly<OnlineCartDto>,
      fetchEvent: (businessRef: BusinessModuleRef, eventId: string) => Promise<OperationResponse<EventProfile>>,
      fetchPublishedEvent: (eventId: string) => Promise<OperationResponse<EventProfile>>,
      fetchDiscountCodes: (businessRef: BusinessModuleRef, code: string) => Promise<OperationResponse<DiscountCodeBase[]>>,
      fetchTicketSupplementTemplates: (businessRef: BusinessModuleRef) => Promise<OperationResponse<TicketSupplementTemplate[]>>,
      fetchSeatingLayoutForEvent: (businessRef: BusinessModuleRef, eventId: string, seatingLayoutId: string) => Promise<OperationResponse<SeatingLayout>>,
      fetchCustomerAccount: (accountId: string) => Promise<OperationResponse<CustomerAccount>>,
      fetchTicketPoolIfAvailable: (businessRef: BusinessModuleRef, eventId: string) => Promise<OperationResponse<Optional<ITicketPool>>>,
      fetchTicketCapacityGroups: (businessRef: BusinessModuleRef, eventId: string) => Promise<OperationResponse<EventTicketCapacityGroups>>,
  ): OnlineCartValidator {
    return new OnlineCartValidator(
        now,
        authenticatedUserId,
        cart,
        this.createCachedFetchEvent(fetchEvent),
        this.createCachedFetchPublishedEvent(fetchPublishedEvent),
        this.createCachedFetchDiscountCodes(fetchDiscountCodes),
        this.createCachedFetchTicketSupplementTemplates(fetchTicketSupplementTemplates),
        this.createCachedFetchSeatingLayoutForEvent(fetchSeatingLayoutForEvent),
        this.createCachedFetchCustomerAccount(fetchCustomerAccount),
        this.createCachedFetchTicketPoolIfAvailable(fetchTicketPoolIfAvailable),
        this.createCachedFetchTicketCapacityGroups(fetchTicketCapacityGroups),
    );
  }

  private static createCachedFetchEvent(
      fetchEvent: (businessRef: BusinessModuleRef, eventId: string) => Promise<OperationResponse<EventProfile>>
  ) {
    const cachedFetchEvent = this.createCachedOperationResponseFetch(
        async (args: FetchEventArgs) => await fetchEvent(args.businessRef, args.eventId),
        Equivalence.make((a, b) => a.businessRef.isSame(b.businessRef) && a.eventId === b.eventId),
        (args, error) => `Failed to fetch event profile for businessRef=${args.businessRef}, eventId=${args.eventId}: ${JSON.stringify(error, null, 2)}`,
    );

    return (businessRef: BusinessModuleRef, eventId: string) => cachedFetchEvent({ businessRef, eventId });
  }

  private static createCachedFetchPublishedEvent(
      fetchPublishedEvent: (eventId: string) => Promise<OperationResponse<EventProfile>>
  ) {
    return this.createCachedOperationResponseFetch(
        async (eventId: string) => await fetchPublishedEvent(eventId),
        Equivalence.string,
        (eventId, error) => `Failed to fetch published event profile for eventId=${eventId}: ${JSON.stringify(error, null, 2)}`,
    );
  }

  private static createCachedFetchDiscountCodes(
      fetchDiscountCodes: (businessRef: BusinessModuleRef, code: string) => Promise<OperationResponse<DiscountCodeBase[]>>
  ) {
    const cachedFetchDiscountCodes = this.createCachedOperationResponseFetch(
        async (args: FetchDiscountCodeArgs) => await fetchDiscountCodes(args.businessRef, args.code),
        Equivalence.make((a, b) => a.businessRef.isSame(b.businessRef) && a.code === b.code),
        (args, error) => `Failed to fetch discount codes for businessRef=${args.businessRef}, code=${args.code}: ${JSON.stringify(error, null, 2)}`,
    );

    return (businessRef: BusinessModuleRef, code: string) => cachedFetchDiscountCodes({ businessRef, code });
  }

  private static createCachedFetchTicketSupplementTemplates(
      fetchTicketSupplementTemplates: (businessRef: BusinessModuleRef) => Promise<OperationResponse<TicketSupplementTemplate[]>>
  ) {
    return this.createCachedOperationResponseFetch(
        async (businessRef: BusinessModuleRef) => await fetchTicketSupplementTemplates(businessRef),
        Equivalence.make((a, b) => a.isSame(b)),
        (businessRef, error) => `Failed to fetch ticket supplement templates for businessRef=${businessRef}: ${JSON.stringify(error, null, 2)}`,
    );
  }

  private static createCachedFetchSeatingLayoutForEvent(
      fetchSeatingLayoutForEvent: (businessRef: BusinessModuleRef, eventId: string, seatingLayoutId: string) => Promise<OperationResponse<SeatingLayout>>
  ) {
    const cachedFetchSeatingLayoutForEvent = this.createCachedOperationResponseFetch(
        async (args: FetchSeatingLayoutArgs) => await fetchSeatingLayoutForEvent(
            args.businessRef,
            args.eventId,
            args.seatingLayoutId,
        ),
        Equivalence.make((a, b) =>
          isSameData(b.businessRef, a.businessRef) &&
          b.eventId === a.eventId &&
          b.seatingLayoutId === a.seatingLayoutId),
        (args, error) => `Failed to fetch seating layout for businessRef=${args.businessRef}, eventId=${args.eventId}, seatingLayoutId=${args.seatingLayoutId}: ${JSON.stringify(error, null, 2)}`,
    );

    return (
        businessRef: BusinessModuleRef,
        eventId: string,
        seatingLayoutId: string,
    ) => cachedFetchSeatingLayoutForEvent({ businessRef, eventId, seatingLayoutId });
  }

  private static createCachedFetchCustomerAccount(
      fetchCustomerAccount: (accountId: string) => Promise<OperationResponse<CustomerAccount>>
  ) {
    return this.createCachedOperationResponseFetch(
        async (accountId: string) => await fetchCustomerAccount(accountId),
        Equivalence.string,
        (accountId, error) => `Failed to fetch customer account for accountId=${accountId}: ${JSON.stringify(error, null, 2)}`,
    );
  }

  private static createCachedFetchTicketPoolIfAvailable(
      fetchTicketPoolIfAvailable: (businessRef: BusinessModuleRef, eventId: string) => Promise<OperationResponse<Optional<ITicketPool>>>
  ) {
    const cachedFetchTicketPoolIfAvailable = this.createCachedOperationResponseFetch(
        async (args: FetchEventArgs) => await fetchTicketPoolIfAvailable(args.businessRef, args.eventId),
        Equivalence.make((a, b) => a.businessRef.isSame(b.businessRef) && a.eventId === b.eventId),
        (args, error) => `Failed to fetch ticket pool for businessRef=${args.businessRef}, eventId=${args.eventId}: ${JSON.stringify(error, null, 2)}`,
    );

    return (businessRef: BusinessModuleRef, eventId: string) => cachedFetchTicketPoolIfAvailable({ businessRef, eventId });
  }

  private static createCachedFetchTicketCapacityGroups(
      fetchTicketCapacityGroups: (businessRef: BusinessModuleRef, eventId: string) => Promise<OperationResponse<EventTicketCapacityGroups>>
  ) {
    const cachedFetchTicketCapacityGroups = this.createCachedOperationResponseFetch(
        async (args: FetchEventArgs) => await fetchTicketCapacityGroups(args.businessRef, args.eventId),
        Equivalence.make((a, b) => a.businessRef.isSame(b.businessRef) && a.eventId === b.eventId),
        (args, error) => `Failed to fetch ticket capacity groups for businessRef=${args.businessRef}, eventId=${args.eventId}: ${JSON.stringify(error, null, 2)}`,
    );

    return (businessRef: BusinessModuleRef, eventId: string) => cachedFetchTicketCapacityGroups({ businessRef, eventId });
  }

  private static createCachedOperationResponseFetch<Args, Value>(
      fetch: (args: Args) => Promise<OperationResponse<Value>>,
      equivalence: Equivalence.Equivalence<Args>,
      errorMessage: (args: Args, error: unknown) => string,
  ): (args: Args) => Effect.Effect<Value, Error, never> {
    return Effect.runSync(Effect.gen(function* () {
      const cacheableFetch = (args: Args) => Effect.gen(function* () {
        const response = yield* Effect.promise(async () => await fetch(args));
        if(!response.success) {
          yield* Effect.fail(new Error(errorMessage(args, response.error)));
        }

        return response.data;
      });

      return yield* Effect.cachedFunction(cacheableFetch, equivalence);
    }));
  }

  validate() {
    return Effect.gen(this, function* () {
      yield* Effect.all([
        this.mustHaveAtLeastOneItem,
        this.mustMatchAuthenticationState,
        this.ifGivenMustBeValidEventId,
        this.ifGivenDiscountCodeMustBeValid,
        this.donationItemsMustBeValid,
        this.productItemsMustBeValid,
        this.ticketItemsMustHaveValidPriceModifier,
        this.ticketItemsWithQuantityMustBeValid,
        this.ticketItemsTemplatesMustMatchDatabase,
        this.ticketItemsMustBeInSellingDateRange,
        this.ticketItemCartLimitsMustNotBeExceeded,
        this.coupleTicketItemQuantitiesMustBeEven,
        this.ticketItemsSupplementsMustMatchDatabase,
        this.ticketCartItemsMustHaveValidSeatIds,
        this.validateItemTypesAgainstOrderCreationStrategy,
        this.validateFormSubmissions,
      ], { discard: true });
    });
  }

  private get mustMatchAuthenticationState() {
    return Effect.gen(this, function* () {
      if(this.authenticatedUserId == null) {
        if(this.cart.customer == null) {
          yield* Effect.fail(new OnlineCartGuestOrderExpectedError());
        }

        const error = this.validateGuestCustomerProfile();
        if(error != null) {
          yield* Effect.fail(new OnlineCartInvalidCustomerProfileError(error));
        }

        return;
      }

      const authenticatedUserId = this.authenticatedUserId;
      const customerAccount = yield* mapFetchError(
          this.fetchCustomerAccount(authenticatedUserId),
          () => new OnlineCartCustomerAccountDoesNotExistError(authenticatedUserId),
      );

      // TODO: (varadiemil) implement validation for new customer setup
      // if(authenticatedUserId != null && this.cart.creatorId !== authenticatedUserId) {
      //   yield* Effect.fail(new OnlineCartCreatorDoesNotMatchAuthenticatedUserError(
      //       authenticatedUserId,
      //       this.cart.creatorId,
      //   ));
      // }

      // TODO: (varadiemil) implement validation for new customer setup
      // if(this.cart.customer != null) {
      //   yield* Effect.fail(new OnlineCartAuthenticatedOrderIsGuestOrderError());
      // }

      const error = this.validateAuthenticatedCustomerProfile(customerAccount);
      if(error != null) {
        yield* Effect.fail(new OnlineCartInvalidCustomerProfileError(error));
      }
    });
  }

  private validateAuthenticatedCustomerProfile(account: CustomerAccount): string | null {
    // TODO: (varadiemil) implement validation for new customer setup
    return null;
  }

  private validateGuestCustomerProfile(): string | null {
    // TODO: (varadiemil) implement validation for new customer setup
    return null;
  }

  /*
  private validateAuthenticatedCustomerProfile(account: CustomerAccount): string | null {
    return this.validateCommonCustomerProfile() ??
      (this.cart.customer.userId !== account.uniqueId ?
        'Customer profile must belong to the authenticated user' :
        null);
  }

  private validateGuestCustomerProfile(): string | null {
    return this.validateCommonCustomerProfile() ??
      (this.cart.customer.userId != null ?
        'Customer profile must not belong to a customer account for a guest order' :
        null);
  }

  private validateCommonCustomerProfile(): string | null {
    if(!this.cart.customer.businessRef.isSame(this.cart.businessRef)) {
      return 'Customer profile businessRef must match the cart businessRef';
    }

    if(!this.cart.customer.isLive) {
      return 'Customer profile must be live';
    }

    if(!this.cart.customer.isOnlineCustomer) {
      return 'Customer profile must be an online customer';
    }

    return null;
  }
  */

  private get mustHaveAtLeastOneItem() {
    return Effect.gen(this, function* () {
      if(this.cart.items.length < 1) {
        yield* Effect.fail(new OnlineCartNoItemsError());
      }
    });
  }

  private get ifGivenMustBeValidEventId() {
    return Effect.gen(this, function* () {
      if(this.cart.eventId == null) {
        return;
      }

      yield* mapFetchError(
          this.fetchEvent(this.cart.businessRef, this.cart.eventId),
          () => new OnlineCartEventDoesNotExistError(),
      );
    });
  }

  private get ifGivenDiscountCodeMustBeValid() {
    return Effect.gen(this, function* () {
      const discountCode = this.cart.voucher;
      if(discountCode == null) {
        return;
      }

      if(!Voucher.verifyType(discountCode) && !OpenAccessPassDetails.verifyType(discountCode) && !GiftCardDetails.verifyType(discountCode)) {
        return yield* Effect.fail(new OnlineCartUnsupportedDiscountCodeError());
      }

      if(OpenAccessPassDetails.assertType(discountCode) &&
          !FlexiblePassProtocol.verifyType(discountCode.protocol)) {
        return yield* Effect.fail(new OnlineCartFlexiblePassInvalidProtocolError());
      }

      const fetchedDiscountCodes = yield* mapFetchError(
          this.fetchDiscountCodes(this.cart.businessRef, discountCode.code),
          () => new OnlineCartDiscountCodeDoesNotExistError(),
      );
      const fetchedDiscountCode = selectDiscountCode(fetchedDiscountCodes, discountCode, this.cart.eventId);

      if(fetchedDiscountCode == null) {
        return yield* Effect.fail(new OnlineCartDiscountCodeDoesNotExistError());
      }

      if(OpenAccessPassDetails.assertType(fetchedDiscountCode) &&
          !FlexiblePassProtocol.verifyType(fetchedDiscountCode.protocol)) {
        return yield* Effect.fail(new OnlineCartFlexiblePassInvalidProtocolError());
      }

      if(!isSameDiscountCode(fetchedDiscountCode, discountCode) ||
          !discountCode.businessRef.isSame(this.cart.businessRef)) {
        return yield* Effect.fail(new OnlineCartDiscountCodeDoesNotMatchError());
      }

      if(GiftCardDetails.assertType(discountCode)) {
        const tickets = this.cart.items.filter(TicketCartItemDto.assertType);
        if(discountCode.status !== GiftCardStatus.Live || this.now.isAfter(discountCode.expirationDate) ||
            !discountCode.remainingValue.isPositive() || tickets.length === 0 ||
            tickets.some((item) => item.unitPrice.currency !== discountCode.remainingValue.currency)) {
          return yield* Effect.fail(new OnlineCartUnsupportedDiscountCodeError());
        }
      }

      if(OpenAccessPassDetails.assertType(discountCode)) {
        yield* this.validateFlexiblePass(discountCode);
      }
    });
  }

  private validateFlexiblePass(pass: OpenAccessPassDetails) {
    return Effect.gen(this, function* () {
      if(pass.status !== OpenAccessPassStatus.Live) {
        return yield* Effect.fail(new OnlineCartFlexiblePassNotLiveError());
      }

      const protocol = pass.protocol;
      if(!FlexiblePassProtocol.assertType(protocol)) {
        return yield* Effect.fail(new OnlineCartFlexiblePassInvalidProtocolError());
      }

      if(this.now.isBefore(protocol.startDate)) {
        return yield* Effect.fail(new OnlineCartFlexiblePassNotYetValidError());
      }

      if(this.now.isAfter(protocol.expirationDate)) {
        return yield* Effect.fail(new OnlineCartFlexiblePassExpiredError());
      }

      const maximumUses = protocol.maxUses;
      if(maximumUses.isFinite && (protocol.uses ?? 0) >= maximumUses.value) {
        return yield* Effect.fail(new OnlineCartFlexiblePassExhaustedError());
      }

      const passVariationId = pass.variation?.uniqueId ?? OpenAccessPass.DEFAULT_VARIATION_ID;
      for(const item of this.cart.items) {
        if(!TicketCartItemDto.assertType(item) || item.eventId == null) {
          continue;
        }

        if(!protocol.isEventAssignedToPass({ eventId: item.eventId, passVariationId })) {
          return yield* Effect.fail(new OnlineCartFlexiblePassIneligibleEventError(item.eventId));
        }
      }
    });
  }

  private get donationItemsMustBeValid() {
    return Effect.gen(this, function* () {
      let donationItem: { index: number, item: DonationCartItemDto } | null = null;

      for(const [index, item] of this.cart.items.entries()) {
        if(!(item instanceof DonationCartItemDto)) {
          continue;
        }

        if(donationItem != null) {
          yield* Effect.fail(new OnlineCartHasMoreThanOneDonationItemError());
        }

        donationItem = { index, item };
      }

      if(donationItem == null) {
        return;
      }

      const error = this.isValidDonationCartItem(donationItem.item);
      if(error) {
        yield* Effect.fail(new OnlineCartHasInvalidDonationItemError(donationItem.index, error));
      }
    });
  }

  private isValidDonationCartItem(item: DonationCartItemDto): void | string {
    if(!item.unitPrice.isPositive()) {
      return 'Donation item unit price must be positive';
    }

    if(item.quantity !== 1) {
      return 'Donation item quantity must be 1';
    }

    if(item.originalUnitPrice != null) {
      return 'Donation item original unit price must be null';
    }

    if(!item.unitPrice.isSame(item.totalPrice)) {
      return 'Donation item total price must be the same as unit price';
    }
  }

  private get productItemsMustBeValid() {
    return Effect.gen(this, function* () {
      for(const [index, item] of this.cart.items.entries()) {
        if(!(item instanceof ProductCartItemDto)) {
          continue;
        }

        const eventId = item.eventId ?? this.cart.eventId;
        if(eventId == null) {
          yield* Effect.fail(new OnlineCartEventIdUndecidableForProductItemError(index));
          return;
        }

        yield* mapFetchError(
            this.fetchEvent(this.cart.businessRef, eventId),
            () => new OnlineCartProductItemEventDoesNotExistError(index),
        );

        const error = this.isValidProductCartItem(item);
        if(error) {
          yield* Effect.fail(new OnlineCartInvalidProductItemError(index, error));
        }

        const event = yield* mapFetchError(
            this.fetchPublishedEvent(eventId),
            () => new OnlineCartProductItemEventDoesNotExistError(index),
        );

        const component = event.findComponentByTypeId<ProductListComponent>(ProductListComponent.TYPE_ID);
        const matchingProduct = component?.products.find((product) => product.uniqueId === item.uniqueId);
        if(!matchingProduct) {
          yield* Effect.fail(new OnlineCartInvalidProductItemError(index, `Product item does not match any product in the event's product list`));
          continue;
        }

        const isSame = isSameProduct(matchingProduct, item.product);
        if(!isSame) {
          yield* Effect.fail(new OnlineCartInvalidProductItemError(index, `Product item does not match the product in the event's product list`));
        }
      }
    });
  }

  private isValidProductCartItem(item: ProductCartItemDto): void | string {
    if(item.quantity < 1) {
      return 'Product item quantity must be at least 1';
    }

    if(item.unitPrice.isNegative()) {
      return 'Product item unit price must not be negative';
    }

    if(item.originalUnitPrice != null && item.originalUnitPrice.isNegative()) {
      return 'Product item original unit price must not be negative';
    }

    if(item.originalUnitPrice != null && item.unitPrice.compareTo(item.originalUnitPrice) > 0) {
      return 'Product item unit price must not be greater than original unit price';
    }
  }

  private get ticketItemsWithQuantityMustBeValid() {
    return Effect.gen(this, function* () {
      for(const [index, item] of this.cart.items.entries()) {
        if(item instanceof TicketCartItemWithQuantityDto) {
          const error = this.validateTicketCartItemWithQuantity(item);
          if(error) {
            yield* Effect.fail(new OnlineCartInvalidTicketItemWithQuantityError(index, error));
          }
        }
      }
    });
  }

  private get ticketItemsMustHaveValidPriceModifier() {
    return Effect.gen(this, function* () {
      for(const [index, item] of this.cart.items.entries()) {
        if(!(item instanceof TicketCartItemDto)) {
          continue;
        }

        if(item.priceModifier != null && item.priceModifier.isNegative()) {
          yield* Effect.fail(new OnlineCartInvalidTicketItemWithQuantityError(
              index,
              'Ticket item price modifier must be positive or zero',
          ));
        }
      }
    });
  }

  private get ticketItemsTemplatesMustMatchDatabase() {
    return Effect.gen(this, function* () {
      for(const [index, item] of this.cart.items.entries()) {
        if(!(item instanceof TicketCartItemDto)) {
          continue;
        }

        const eventId = item.eventId;
        if(eventId == null) {
          return yield* Effect.fail(new OnlineCartUnexpectedError(new Error(`TicketCartItemDto has null eventId`)));
        }

        const template = item.ticket;
        const event = yield* mapFetchError(
            this.fetchPublishedEvent(eventId),
            () => new OnlineCartTicketTemplateDoesNotExistError(index, template.uniqueId),
        );

        const ticketAsPassProtocol = TicketAsPassEventBookingProtocol.assertType(event.bookingProtocol) ?
          event.bookingProtocol :
          null;
        const passTemplateIds = ticketAsPassProtocol?.passTemplateIds ?? null;
        if(ticketAsPassProtocol != null && (passTemplateIds == null || passTemplateIds.length === 0)) {
          yield* Effect.fail(new OnlineCartTicketAsPassProtocolMissingPassTemplateIdError(eventId));
          continue;
        }

        const matchingTemplate = event.findTicketById(template.uniqueId);
        if(!matchingTemplate) {
          yield* Effect.fail(new OnlineCartTicketTemplateDoesNotExistError(index, template.uniqueId));
          continue;
        }

        if(ticketAsPassProtocol != null && passTemplateIds != null) {
          const cartPassComponent = template.findComponentByTypeId<OpenAccessPassComponent>(
              OpenAccessPassComponent.TYPE_ID,
          );
          if(template.hasSideEffect(OrderSideEffectType.CreateSingleOpenAccessPass) && cartPassComponent == null) {
            yield* Effect.fail(new OnlineCartTicketAsPassCartTemplateMissingOpenAccessPassComponentError(
                eventId,
                index,
                template.uniqueId,
            ));
            continue;
          }

          const publishedPassComponent = matchingTemplate.findComponentByTypeId<OpenAccessPassComponent>(
              OpenAccessPassComponent.TYPE_ID,
          );
          if(matchingTemplate.hasSideEffect(OrderSideEffectType.CreateSingleOpenAccessPass) && publishedPassComponent == null) {
            yield* Effect.fail(new OnlineCartTicketAsPassPublishedTemplateMissingOpenAccessPassComponentError(
                eventId,
                index,
                template.uniqueId,
            ));
            continue;
          }

          if(cartPassComponent != null) {
            const cartPassTemplateId = cartPassComponent.getTemplateId();
            if(!passTemplateIds.includes(cartPassTemplateId)) {
              yield* Effect.fail(new OnlineCartTicketAsPassTemplateIdMismatchError(
                  'cart',
                  eventId,
                  index,
                  template.uniqueId,
                  passTemplateIds,
                  [cartPassTemplateId],
              ));
              continue;
            }
          }

          if(publishedPassComponent != null) {
            const publishedPassTemplateId = publishedPassComponent.getTemplateId();
            if(!passTemplateIds.includes(publishedPassTemplateId)) {
              yield* Effect.fail(new OnlineCartTicketAsPassTemplateIdMismatchError(
                  'publishedEvent',
                  eventId,
                  index,
                  template.uniqueId,
                  passTemplateIds,
                  [publishedPassTemplateId],
              ));
              continue;
            }
          }

          const multiPassTemplateIds = template.sideEffects
              .filter((e): e is CreateMultiOpenAccessPassSideEffect => CreateMultiOpenAccessPassSideEffect.assertType(e))
              .map((e) => e.templateId);

          if(!multiPassTemplateIds.every((e) => passTemplateIds.includes(e))) {
            yield* Effect.fail(new OnlineCartTicketAsPassTemplateIdMismatchError(
                'cart',
                eventId,
                index,
                template.uniqueId,
                passTemplateIds,
                multiPassTemplateIds,
            ));
            continue;
          }
        }

        if(matchingTemplate.status !== TicketTemplateStatus.Live) {
          yield* Effect.fail(new OnlineCartTicketTemplateNotLiveError(index, template.uniqueId));
          continue;
        }

        if(!matchingTemplate.isPurchasable) {
          yield* Effect.fail(new OnlineCartTicketTemplateNotPurchasableError(index, template.uniqueId));
          continue;
        }

        const isSame = isSameTicketTemplateAsEvent({
          cartTemplate: template,
          eventTemplate: matchingTemplate,
        });
        if(!isSame) {
          yield* Effect.fail(new OnlineCartTicketTemplateDoesNotMatchError(index, template.uniqueId));
        }
      }
    });
  }

  private get ticketItemsMustBeInSellingDateRange() {
    return Effect.gen(this, function* () {
      for(const [index, item] of this.cart.items.entries()) {
        if(!(item instanceof TicketCartItemDto)) {
          continue;
        }

        const eventId = item.eventId;
        if(eventId == null) {
          return yield* Effect.fail(new OnlineCartUnexpectedError(new Error(`TicketCartItemDto has null eventId`)));
        }

        const ticketTemplateId = item.ticket.uniqueId;
        const event = yield* mapFetchError(
            this.fetchPublishedEvent(eventId),
            () => new OnlineCartTicketTemplateDoesNotExistError(index, ticketTemplateId),
        );

        const template = event.findTicketById(ticketTemplateId);
        if(!template) {
          yield* Effect.fail(new OnlineCartTicketTemplateDoesNotExistError(index, ticketTemplateId));
          continue;
        }

        if(template.dateSpan.contains(this.now)) {
          continue;
        }

        const hasCascadingAvailability = yield* this.hasCascadingAvailabilityBeforeSellingStart(template, event);
        if(hasCascadingAvailability) {
          continue;
        }

        yield* Effect.fail(new OnlineCartTicketTemplateNotInSellingDateRangeError(
            index,
            ticketTemplateId,
        ));
      }
    });
  }

  private hasCascadingAvailabilityBeforeSellingStart(template: TicketTemplate, event: EventProfile) {
    return Effect.gen(this, function* () {
      const availabilityModifier = template.availabilityModifier;
      if(!(availabilityModifier instanceof CascadingTicketAvailabilityModifier)) {
        return false;
      }

      if(!template.dateSpan.from.isAfter(this.now)) {
        return false;
      }

      const dependentTemplate = event.findTicketById(availabilityModifier.dependentTicketTemplateId);
      if(dependentTemplate == null) {
        return false;
      }

      const ticketPool = yield* Effect.matchEffect(this.fetchTicketPoolIfAvailable(event.businessRef, event.uniqueId), {
        onFailure: () => Effect.succeed(Optional.none<ITicketPool>()),
        onSuccess: (pool) => Effect.succeed(pool),
      });
      if(ticketPool.isNone) {
        return false;
      }

      const capacityGroups = yield* this.fetchTicketCapacityGroups(event.businessRef, event.uniqueId);
      const remaining = capacityGroups.getRemainingTicketCapacity(dependentTemplate, ticketPool.value);
      if(remaining.isInfinite) {
        return false;
      }

      return remaining.value <= OnlineCartValidator.CASCADING_AVAILABILITY_REMAINING_THRESHOLD;
    });
  }

  private get ticketItemCartLimitsMustNotBeExceeded() {
    return Effect.gen(this, function* () {
      const ticketCountByTemplateId = this.getTicketCountByTemplateId(
          (item) => item instanceof TicketCartItemWithQuantityDto,
      );

      for(const { ticket, count } of ticketCountByTemplateId.values()) {
        if(ticket.specialOffer != null) {
          continue;
        }

        const cartLimit = ticket.findComponentByTypeId<TicketCartLimitComponent>(
            TicketCartLimitComponent.TYPE_ID,
        )?.cartLimit;

        if(cartLimit == null || cartLimit.compareTo(count) >= 0) {
          continue;
        }

        yield* Effect.fail(new OnlineCartTicketTemplateCartLimitExceededError(
            ticket.uniqueId,
            cartLimit.value,
            count,
        ));
      }
    });
  }

  private get coupleTicketItemQuantitiesMustBeEven() {
    return Effect.gen(this, function* () {
      const ticketCountByTemplateId = this.getTicketCountByTemplateId(
          (item) => item.ticket.specialOffer === TicketSpecialOffer.CoupleTicket,
      );

      for(const { ticket, count } of ticketCountByTemplateId.values()) {
        if(count % 2 !== 0) {
          yield* Effect.fail(new OnlineCartCoupleTicketQuantityIsOddError(
              ticket.uniqueId,
              count,
          ));
        }
      }
    });
  }

  private getTicketCountByTemplateId(
      shouldInclude: (item: TicketCartItemDto) => boolean,
  ): Map<string, { ticket: TicketTemplate, count: number }> {
    const ticketCountByTemplateId = new Map<string, { ticket: TicketTemplate, count: number }>();

    for(const item of this.cart.items) {
      if(!(item instanceof TicketCartItemDto) || !shouldInclude(item)) {
        continue;
      }

      const current = ticketCountByTemplateId.get(item.ticket.uniqueId);
      ticketCountByTemplateId.set(item.ticket.uniqueId, {
        ticket: item.ticket,
        count: (current?.count ?? 0) + this.getTicketItemQuantity(item),
      });
    }

    return ticketCountByTemplateId;
  }

  private getTicketItemQuantity(item: TicketCartItemDto): number {
    if(item instanceof TicketCartItemWithQuantityDto) {
      return item.quantity;
    }

    if(item instanceof TicketCartItemWithSeatingDto) {
      return item.quantity;
    }

    return 1;
  }

  private get ticketItemsSupplementsMustMatchDatabase() {
    return Effect.gen(this, function* () {
      const hasSupplements = this.cart.items.some((item) => {
        return item instanceof TicketCartItemDto && item.supplement != null;
      });
      if(!hasSupplements) {
        return;
      }

      const ticketSupplementTemplates = yield* mapFetchError(
          this.fetchTicketSupplementTemplates(this.cart.businessRef),
          (error) => new Error(`Failed to fetch ticket supplement templates for businessRef=${this.cart.businessRef}: ${JSON.stringify(error, null, 2)}`),
      );

      for(const [index, item] of this.cart.items.entries()) {
        if(!(item instanceof TicketCartItemDto) || item.supplement == null) {
          continue;
        }

        const supplement = item.supplement;

        const matchingTemplate = ticketSupplementTemplates.find((template) => template.uniqueId === supplement.uniqueId);
        if(!matchingTemplate) {
          yield* Effect.fail(new OnlineCartTicketSupplementTemplateDoesNotExistError(index, supplement.uniqueId));
          continue;
        }

        const isSame = isSameTicketSupplementTemplate(matchingTemplate, supplement);
        if(!isSame) {
          yield* Effect.fail(new OnlineCartTicketSupplementTemplateDoesNotMatchError(index, supplement.uniqueId));
        }
      }
    });
  }

  private validateTicketCartItemWithQuantity(item: TicketCartItemWithQuantityDto): void | string {
    if(item.quantity < 1) {
      return 'Ticket item quantity must be at least 1';
    }

    if(item.unitPrice.isNegative()) {
      return 'Ticket item unit price must not be negative';
    }
  }

  private get ticketCartItemsMustHaveValidSeatIds() {
    return Effect.gen(this, function* () {
      for(const [index, item] of this.cart.items.entries()) {
        if(!(item instanceof TicketCartItemWithSeatingDto)) {
          continue;
        }

        const eventId = item.eventId;
        if(eventId == null) {
          return yield* Effect.fail(new OnlineCartUnexpectedError(new Error(`TicketCartItemWithSeatingDto has null eventId`)));
        }

        const event = yield* mapFetchError(
            this.fetchPublishedEvent(eventId),
            () => new OnlineCartEventDoesNotExistError(),
        );

        const seatingLayoutId = event.findComponentByTypeId<SeatingSettingsComponent>(SeatingSettingsComponent.TYPE_ID)?.seatingLayoutId;
        if(!seatingLayoutId) {
          yield* Effect.fail(new OnlineCartInvalidTicketItemWithQuantityError(index, `Event with eventId=${eventId} does not have a seating layout`));
          return;
        }

        const seatingLayout = yield* mapFetchError(
            this.fetchSeatingLayoutForEvent(this.cart.businessRef, eventId, seatingLayoutId),
            () => new OnlineCartSeatingLayoutDoesNotExistError(index, seatingLayoutId),
        );

        for(const seatId of item.seats) {
          const seat = seatingLayout.findSeatById(seatId);
          if(!seat) {
            yield* Effect.fail(new OnlineCartSeatDoesNotExistError(index, seatId));
            return;
          }
        }
      }
    });
  }

  private get validateItemTypesAgainstOrderCreationStrategy() {
    return Effect.gen(this, function* () {
      const strategy = this.cart.strategy;

      for(const [index, item] of this.cart.items.entries()) {
        const isCompatible = this.isItemCompatibleWithOrderCreationStrategy(item, strategy);
        if(!isCompatible) {
          yield* Effect.fail(new OnlineCartItemsIncompatibleWithOrderCreationStrategyError(index, strategy));
        }
      }
    });
  }

  private isItemCompatibleWithOrderCreationStrategy(item: CartItemDto, strategy: OrderCreationStrategy): boolean {
    switch(strategy) {
      case OrderCreationStrategy.TicketOrder:
        return TicketCartItemDto.verifyType(item) || ProductCartItemDto.verifyType(item) || DonationCartItemDto.verifyType(item);
      case OrderCreationStrategy.ProductOrder:
        return ProductCartItemDto.verifyType(item);
      case OrderCreationStrategy.GenericOrder:
        return true;
      default:
        assertUnreachable(strategy);
    }
  }

  private get validateFormSubmissions() {
    return Effect.gen(this, function* () {
      const questionReferences = getCartFormQuestionReferences(this.cart.items);
      const events = yield* this.fetchReferencedPublishedEvents(questionReferences.eventIds);
      const questionCatalog = createFormQuestionCatalog(events);
      const submittedQuestionIds = yield* validateSubmittedFormAnswers(
          this.cart.formSubmissions ?? [],
          questionCatalog,
      );

      yield* validateRequiredFormSubmissionAnswers(
          questionCatalog,
          submittedQuestionIds,
          questionReferences.ticketIds,
      );
    });
  }

  private fetchReferencedPublishedEvents(eventIds: readonly string[]) {
    return Effect.all(eventIds.map((eventId) => mapFetchError(
        this.fetchPublishedEvent(eventId),
        () => new OnlineCartEventDoesNotExistError(),
    )), { discard: false });
  }

  async runValidation(): Promise<VoidResult<OnlineCartValidationError>> {
    const withErrorHandling = Effect.matchEffect(this.validate(), {
      onFailure: (error) => Effect.succeed(VoidResult.error<OnlineCartValidationError>(error)),
      onSuccess: () => Effect.succeed(VoidResult.ok<OnlineCartValidationError>()),
    });

    try {
      return await Effect.runPromise(withErrorHandling);
    }
    catch(error) {
      const wrappedError = error instanceof Error ?
        error :
        new Error(`Unknown error during cart validation: ${JSON.stringify(error, null, 2)}`);

      return VoidResult.error<OnlineCartValidationError>(new OnlineCartUnexpectedError(wrappedError));
    }
  }
}

function getCartFormQuestionReferences(items: readonly CartItemDto[]): CartFormQuestionReferences {
  const eventIds = new Set<string>();
  const ticketIds = new Set<string>();

  for(const item of items) {
    if(!(item instanceof TicketCartItemDto)) {
      continue;
    }

    if(item.eventId) {
      eventIds.add(item.eventId);
    }

    ticketIds.add(item.ticket.uniqueId);
  }

  return {
    eventIds: Array.from(eventIds),
    ticketIds,
  };
}

function createFormQuestionCatalog(events: readonly EventProfile[]): FormQuestionCatalog {
  const eventQuestionsById = new Map<string, FormInputField>();
  const requiredEventQuestions: FormInputField[] = [];
  const ticketQuestionsByTicketId = new Map<string, Map<string, FormInputField>>();
  const requiredTicketQuestionsByTicketId = new Map<string, FormInputField[]>();

  const addEventQuestion = (question: FormInputField) => {
    eventQuestionsById.set(question.uniqueId, question);
    if(question.isRequired) {
      requiredEventQuestions.push(question);
    }
  };

  const addTicketQuestion = (ticketId: string, question: FormInputField) => {
    const ticketQuestions = getOrCreateMapValue(
        ticketQuestionsByTicketId,
        ticketId,
        () => new Map<string, FormInputField>(),
    );

    ticketQuestions.set(question.uniqueId, question);
    if(question.isRequired) {
      getOrCreateMapValue(requiredTicketQuestionsByTicketId, ticketId, () => []).push(question);
    }
  };

  for(const event of events) {
    const eventLevelQuestions = event.findComponentByTypeId<FormQuestionsComponent>(FormQuestionsComponent.TYPE_ID)?.questions ?? [];
    for(const question of eventLevelQuestions) {
      addEventQuestion(question);
    }

    const tickets = event.findComponentByTypeId<TicketTemplateComponent>(TicketTemplateComponent.TYPE_ID)?.tickets ?? [];
    for(const ticket of tickets) {
      const ticketLevelQuestions = ticket.findComponentByTypeId<FormQuestionsComponent>(FormQuestionsComponent.TYPE_ID)?.questions ?? [];
      for(const question of ticketLevelQuestions) {
        addTicketQuestion(ticket.uniqueId, question);
      }
    }
  }

  return {
    eventQuestionsById,
    requiredEventQuestions,
    ticketQuestionsByTicketId,
    requiredTicketQuestionsByTicketId,
  };
}

function validateSubmittedFormAnswers(
    submissions: readonly FormSubmission[],
    questionCatalog: FormQuestionCatalog,
) {
  return Effect.gen(function* () {
    const submittedEventQuestionIds = new Set<string>();
    const submittedTicketQuestionIdsByTicketId = new Map<string, Set<string>>();

    for(const [submissionIndex, submission] of submissions.entries()) {
      if(submission.ticketTemplateId == null) {
        const submittedQuestionIds = yield* validateFormSubmissionAnswers({
          submissionIndex,
          answers: submission.answers,
          questionsById: questionCatalog.eventQuestionsById,
          createQuestionMissingError: (questionId) => OnlineCartFormQuestionDoesNotExist.eventLevel(submissionIndex, questionId),
          createTypeMismatchError: (questionId, typeMismatch) => OnlineCartHasMismatchedSubmissionAnswerType.eventLevel(
              submissionIndex,
              questionId,
              typeMismatch.expectedType,
              typeMismatch.actualType,
          ),
        });

        for(const questionId of submittedQuestionIds) {
          submittedEventQuestionIds.add(questionId);
        }

        continue;
      }

      const ticketTemplateId = submission.ticketTemplateId;
      const submittedQuestionIds = yield* validateFormSubmissionAnswers({
        submissionIndex,
        answers: submission.answers,
        questionsById: questionCatalog.ticketQuestionsByTicketId.get(ticketTemplateId),
        createQuestionMissingError: (questionId) => OnlineCartFormQuestionDoesNotExist.ticketLevel(submissionIndex, questionId, ticketTemplateId),
        createTypeMismatchError: (questionId, typeMismatch) => OnlineCartHasMismatchedSubmissionAnswerType.ticketLevel(
            submissionIndex,
            questionId,
            typeMismatch.expectedType,
            typeMismatch.actualType,
            ticketTemplateId,
        ),
      });

      const submittedTicketQuestionIds = getOrCreateMapValue(
          submittedTicketQuestionIdsByTicketId,
          ticketTemplateId,
          () => new Set<string>(),
      );

      for(const questionId of submittedQuestionIds) {
        submittedTicketQuestionIds.add(questionId);
      }
    }

    return {
      eventQuestionIds: submittedEventQuestionIds,
      ticketQuestionIdsByTicketId: submittedTicketQuestionIdsByTicketId,
    };
  });
}

function validateFormSubmissionAnswers(args: FormSubmissionAnswerValidationArgs) {
  return Effect.gen(function* () {
    const submittedQuestionIds = new Set<string>();

    for(const answer of args.answers) {
      const question = args.questionsById?.get(answer.questionId);
      if(question == null) {
        yield* Effect.fail(args.createQuestionMissingError(answer.questionId));
        continue;
      }

      submittedQuestionIds.add(answer.questionId);

      const typeMismatchError = getFormSubmissionAnswerTypeMismatch(answer.questionId, question, answer);
      if(typeMismatchError instanceof Error) {
        yield* Effect.fail(typeMismatchError);
        continue;
      }

      if(typeMismatchError) {
        yield* Effect.fail(args.createTypeMismatchError(answer.questionId, typeMismatchError));
      }
    }

    return submittedQuestionIds;
  });
}

function validateRequiredFormSubmissionAnswers(
    questionCatalog: FormQuestionCatalog,
    submittedQuestionIds: SubmittedFormQuestionIds,
    ticketIdsInCart: ReadonlySet<string>,
) {
  return Effect.gen(function* () {
    for(const question of questionCatalog.requiredEventQuestions) {
      if(!submittedQuestionIds.eventQuestionIds.has(question.uniqueId)) {
        yield* Effect.fail(OnlineCartDoesNotHaveSubmissionAnswerForRequiredQuestion.eventLevel(question.uniqueId));
      }
    }

    for(const [ticketId, questions] of questionCatalog.requiredTicketQuestionsByTicketId.entries()) {
      if(!ticketIdsInCart.has(ticketId)) {
        continue;
      }

      const submittedTicketQuestionIds = submittedQuestionIds.ticketQuestionIdsByTicketId.get(ticketId);
      for(const question of questions) {
        if(!submittedTicketQuestionIds?.has(question.uniqueId)) {
          yield* Effect.fail(OnlineCartDoesNotHaveSubmissionAnswerForRequiredQuestion.ticketLevel(question.uniqueId, ticketId));
        }
      }
    }
  });
}

function getFormSubmissionAnswerTypeMismatch(
    questionId: string,
    question: FormInputField,
    answer: FormSubmissionAnswer,
): FormSubmissionAnswerTypeMismatch | Error | null {
  const matcher = formSubmissionAnswerTypeMatchers.find((type) => type.matchesQuestion(question));
  if(matcher == null) {
    return new Error(`Unsupported form question type for questionId=${questionId}`);
  }

  if(matcher.matchesAnswer(answer)) {
    return null;
  }

  return {
    expectedType: matcher.expectedType,
    actualType: answer.$typeId,
  };
}

function mapFetchError<Value, MappedError>(
    effect: Effect.Effect<Value, Error, never>,
    createError: (error: Error) => MappedError,
): Effect.Effect<Value, MappedError, never> {
  return Effect.mapError(effect, createError);
}

function getOrCreateMapValue<K, V>(map: Map<K, V>, key: K, createValue: () => V): V {
  const existingValue = map.get(key);
  if(existingValue != null) {
    return existingValue;
  }

  const value = createValue();
  map.set(key, value);

  return value;
}

function isSameProduct(a: Product, b: Product): boolean {
  if(a === b) {
    return true;
  }

  return a.uniqueId === b.uniqueId &&
    isSameData(a.businessRef, b.businessRef) &&
    isSameInstant(a.creationDate, b.creationDate) &&
    isSameData(a.names, b.names) &&
    isSameData(a.richDescriptions, b.richDescriptions) &&
    isSameData(a.coverImage, b.coverImage) &&
    isSameData(a.price, b.price) &&
    isSameData(a.originalPrice, b.originalPrice) &&
    a.isPurchasable === b.isPurchasable &&
    a.status === b.status &&
    isSameComponentList(a.components, b.components) &&
    isSameList(a.sideEffects, b.sideEffects, isSameData) &&
    a.overrideVat === b.overrideVat &&
    a.type === b.type &&
    a.assignable === b.assignable;
}

// TODO(anyone): price and originalPrice can be different.
export function isSameTicketTemplateAsEvent({ cartTemplate, eventTemplate }: {
  cartTemplate: TicketTemplate,
  eventTemplate: TicketTemplate
}): boolean {
  if(cartTemplate === eventTemplate) {
    return true;
  }

  return cartTemplate.uniqueId === eventTemplate.uniqueId &&
    isSameData(cartTemplate.names, eventTemplate.names) &&
    isSameData(cartTemplate.richDescriptions, eventTemplate.richDescriptions) &&
    isSameDateSpan(cartTemplate.dateSpan, eventTemplate.dateSpan) &&
    isSameDateSpanOrNull(cartTemplate.scanDateSpan, eventTemplate.scanDateSpan) &&
    isSameData(cartTemplate.price, eventTemplate.price) &&
    isSameData(cartTemplate.originalPrice, eventTemplate.originalPrice) &&
    isSameData(cartTemplate.applicationFee, eventTemplate.applicationFee) &&
    cartTemplate.specialOffer === eventTemplate.specialOffer &&
    cartTemplate.overrideVat === eventTemplate.overrideVat &&
    isSameData(cartTemplate.quantity, eventTemplate.quantity) &&
    isSameData(cartTemplate.minQuantity, eventTemplate.minQuantity) &&
    cartTemplate.isPurchasableLegacy === eventTemplate.isPurchasableLegacy &&
    cartTemplate.isLimited === eventTemplate.isLimited &&
    cartTemplate.isPublicLegacy === eventTemplate.isPublicLegacy &&
    cartTemplate.isPurchasableBySelfLegacy === eventTemplate.isPurchasableBySelfLegacy &&
    cartTemplate.status === eventTemplate.status &&
    cartTemplate.scopeLegacy === eventTemplate.scopeLegacy &&
    isSameComponentList(cartTemplate.components, eventTemplate.components) &&
    isSameList(cartTemplate.sideEffects, eventTemplate.sideEffects, isSameData) &&
    isSameList(cartTemplate.siblingTicketTemplateIds, eventTemplate.siblingTicketTemplateIds) &&
    cartTemplate.ticketSupplementTemplateId === eventTemplate.ticketSupplementTemplateId &&
    isSameData(cartTemplate.availabilityModifier, eventTemplate.availabilityModifier) &&
    cartTemplate.channel === eventTemplate.channel;
}

function isSameTicketSupplementTemplate(a: TicketSupplementTemplate, b: TicketSupplementTemplate): boolean {
  return isSameData(a, b);
}

function selectDiscountCode(
    discountCodes: readonly DiscountCodeBase[],
    cartDiscountCode: DiscountCodeBase,
    eventId: string | null,
): DiscountCodeBase | undefined {
  const cartHasPromoCode = EventPromoCode.assertType(cartDiscountCode) ||
    TrackingPromoCode.assertType(cartDiscountCode) ||
    BusinessPromoCode.assertType(cartDiscountCode);

  if(!cartHasPromoCode) {
    return discountCodes.find((discountCode) => discountCode.uniqueId === cartDiscountCode.uniqueId && discountCode.$typeId === cartDiscountCode.$typeId);
  }

  let result: DiscountCodeBase | undefined;

  for(const discountCode of discountCodes) {
    if(EventPromoCode.assertType(discountCode)) {
      if(discountCode.eventId === eventId) {
        result = discountCode;
        break;
      }
    }
    else if(TrackingPromoCode.assertType(discountCode)) {
      result = discountCode;
      if(discountCode.eventId === eventId) {
        break;
      }
    }
    else if(BusinessPromoCode.assertType(discountCode)) {
      result = discountCode;
      break;
    }
  }

  return result;
}

function isSameDiscountCode(a: DiscountCodeBase, b: DiscountCodeBase): boolean {
  if(a === b) {
    return true;
  }

  if(GiftCardDetails.assertType(a) && GiftCardDetails.assertType(b)) {
    return a.isSame(b);
  }

  if(Voucher.assertType(a) && Voucher.assertType(b)) {
    return isSameVoucher(a, b);
  }

  if(OpenAccessPassDetails.assertType(a) && OpenAccessPassDetails.assertType(b)) {
    return a.isSame(b);
  }

  return false;
}

function isSameVoucher(a: Voucher, b: Voucher): boolean {
  if(a === b) {
    return true;
  }

  if(a.$typeId !== b.$typeId) {
    return false;
  }

  if(a instanceof BusinessPromoCode && b instanceof BusinessPromoCode) {
    return isSameBusinessPromoCode(a, b);
  }

  if(a instanceof EventPromoCode && b instanceof EventPromoCode) {
    return isSameEventPromoCode(a, b);
  }

  if(a instanceof TrackingPromoCode && b instanceof TrackingPromoCode) {
    return isSameTrackingPromoCode(a, b);
  }

  if(a instanceof OpenTicketPass && b instanceof OpenTicketPass) {
    return isSameOpenTicketPass(a, b);
  }

  if(a instanceof TicketGiftCard && b instanceof TicketGiftCard) {
    return isSameTicketGiftCard(a, b);
  }

  return false;
}

function isSameBusinessPromoCode(a: BusinessPromoCode, b: BusinessPromoCode): boolean {
  if(a === b) {
    return true;
  }

  return a.uniqueId === b.uniqueId &&
    a.code === b.code &&
    isSameData(a.businessRef, b.businessRef) &&
    isSameInstant(a.creationDate, b.creationDate) &&
    a.status === b.status &&
    isSameInstantOrNull(a.usedAt, b.usedAt) &&
    isSameInstantOrNull(a.startDate, b.startDate) &&
    isSameInstantOrNull(a.expirationDate, b.expirationDate) &&
    isSameData(a.protocol, b.protocol) &&
    a.maxUses === b.maxUses &&
    a.uses === b.uses &&
    isSameData(a.descriptions, b.descriptions) &&
    isSameComponentList(a.components, b.components);
}

function isSameEventPromoCode(a: EventPromoCode, b: EventPromoCode): boolean {
  if(a === b) {
    return true;
  }

  return a.uniqueId === b.uniqueId &&
    a.code === b.code &&
    isSameData(a.businessRef, b.businessRef) &&
    isSameInstant(a.creationDate, b.creationDate) &&
    a.status === b.status &&
    isSameInstantOrNull(a.usedAt, b.usedAt) &&
    isSameInstantOrNull(a.startDate, b.startDate) &&
    isSameInstantOrNull(a.expirationDate, b.expirationDate) &&
    a.eventId === b.eventId &&
    isSameList(a.ticketId, b.ticketId) &&
    isSameData(a.protocol, b.protocol) &&
    a.maxUses === b.maxUses &&
    a.uses === b.uses &&
    isSameData(a.descriptions, b.descriptions) &&
    isSameComponentList(a.components, b.components);
}

function isSameTrackingPromoCode(a: TrackingPromoCode, b: TrackingPromoCode): boolean {
  if(a === b) {
    return true;
  }

  return a.uniqueId === b.uniqueId &&
    a.code === b.code &&
    isSameData(a.businessRef, b.businessRef) &&
    isSameInstant(a.creationDate, b.creationDate) &&
    a.status === b.status &&
    isSameInstantOrNull(a.usedAt, b.usedAt) &&
    isSameInstantOrNull(a.startDate, b.startDate) &&
    isSameInstantOrNull(a.expirationDate, b.expirationDate) &&
    a.eventId === b.eventId &&
    isSameList(a.ticketId, b.ticketId) &&
    a.maxUses === b.maxUses &&
    a.uses === b.uses &&
    isSameData(a.descriptions, b.descriptions) &&
    isSameComponentList(a.components, b.components);
}

function isSameOpenTicketPass(a: OpenTicketPass, b: OpenTicketPass): boolean {
  if(a === b) {
    return true;
  }

  return a.uniqueId === b.uniqueId &&
    isSameData(a.businessRef, b.businessRef) &&
    isSameInstant(a.creationDate, b.creationDate) &&
    a.orderId === b.orderId &&
    a.customerId === b.customerId &&
    a.productId === b.productId &&
    a.status === b.status &&
    isSameInstantOrNull(a.usedAt, b.usedAt) &&
    isSameInstantOrNull(a.startDate, b.startDate) &&
    isSameInstantOrNull(a.expirationDate, b.expirationDate) &&
    isSameList(a.includedEvents, b.includedEvents) &&
    isSameList(a.excludedEvents, b.excludedEvents) &&
    isSameList(a.ticketId, b.ticketId) &&
    isSameData(a.productPrice, b.productPrice) &&
    a.maxUses === b.maxUses &&
    a.uses === b.uses &&
    a.type === b.type &&
    a.serialNumber === b.serialNumber &&
    a.userId === b.userId &&
    a.isGuestOrder === b.isGuestOrder &&
    isSameComponentList(a.components, b.components);
}

function isSameTicketGiftCard(a: TicketGiftCard, b: TicketGiftCard): boolean {
  if(a === b) {
    return true;
  }

  return a.uniqueId === b.uniqueId &&
    isSameData(a.businessRef, b.businessRef) &&
    isSameInstant(a.creationDate, b.creationDate) &&
    a.orderId === b.orderId &&
    a.customerId === b.customerId &&
    a.productId === b.productId &&
    a.status === b.status &&
    isSameInstantOrNull(a.usedAt, b.usedAt) &&
    isSameInstantOrNull(a.startDate, b.startDate) &&
    isSameInstantOrNull(a.expirationDate, b.expirationDate) &&
    a.serialNumber === b.serialNumber &&
    a.userId === b.userId &&
    a.isGuestOrder === b.isGuestOrder &&
    isSameList(a.includedEvents, b.includedEvents) &&
    isSameList(a.excludedEvents, b.excludedEvents) &&
    isSameData(a.value, b.value) &&
    isSameData(a.productPrice, b.productPrice) &&
    isSameData(a.usedValue, b.usedValue) &&
    isSameComponentList(a.components, b.components);
}

function isSameComponentList(a: ComponentList, b: ComponentList): boolean {
  if(a === b) {
    return true;
  }

  return isSameList(getComponents(a), getComponents(b), isSameComponent);
}

function getComponents(list: ComponentList): Component[] {
  const components: Component[] = [];
  list.forEach((component) => components.push(component));

  return components;
}

function isSameComponent(a: Component, b: Component): boolean {
  if(a === b) {
    return true;
  }

  if(a.$typeId !== b.$typeId) {
    return false;
  }

  if(a instanceof OpenTicketPassComponent && b instanceof OpenTicketPassComponent) {
    return isSameOpenTicketPassComponent(a, b);
  }

  if(a instanceof TicketGiftCardComponent && b instanceof TicketGiftCardComponent) {
    return isSameTicketGiftCardComponent(a, b);
  }

  return isSameData(a, b);
}

function isSameOpenTicketPassComponent(a: OpenTicketPassComponent, b: OpenTicketPassComponent): boolean {
  if(a === b) {
    return true;
  }

  return isSameList(a.includedEvents, b.includedEvents) &&
    isSameList(a.excludedEvents, b.excludedEvents) &&
    isSameList(a.ticketId, b.ticketId) &&
    isSameInstantOrNull(a.startDate, b.startDate) &&
    isSameInstantOrNull(a.expirationDate, b.expirationDate) &&
    a.maxUses === b.maxUses &&
    a.type === b.type;
}

function isSameTicketGiftCardComponent(a: TicketGiftCardComponent, b: TicketGiftCardComponent): boolean {
  if(a === b) {
    return true;
  }

  return isSameList(a.includedEvents, b.includedEvents) &&
    isSameList(a.excludedEvents, b.excludedEvents) &&
    isSameData(a.value, b.value) &&
    isSameInstantOrNull(a.startDate, b.startDate) &&
    isSameInstantOrNull(a.expirationDate, b.expirationDate);
}

function isSameDateSpan(a: DateSpan, b: DateSpan): boolean {
  if(a === b) {
    return true;
  }

  return isSameInstant(a.from, b.from) &&
    isSameInstant(a.to, b.to);
}

function isSameDateSpanOrNull(a: DateSpan | null, b: DateSpan | null): boolean {
  if(a == null || b == null) {
    return a === b;
  }

  return isSameDateSpan(a, b);
}

function isSameInstant(a: Dayjs, b: Dayjs): boolean {
  return TimeZone.isSameInstant(a, b);
}

function isSameInstantOrNull(a: Dayjs | null, b: Dayjs | null): boolean {
  return TimeZone.isSameInstantOrNull(a, b);
}
